import React from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'

// Types
export interface TimeOption {
  value: string // HH:MM format (24-hour internal format)
  label: string // Display format (12h or 24h based on preference)
}

export interface TimeRangeValue {
  startTime: string // HH:MM format
  endTime: string // HH:MM format
}

export interface TimeRangeSelectorProps {
  value: TimeRangeValue
  onChange: (value: TimeRangeValue) => void
  onFormatToggle?: (show24Hours: boolean) => void
  showFormatToggle?: boolean // Show "Show 24 Hours" toggle
  disabled?: boolean
  className?: string
  minHour?: number // Minimum hour (0-23)
  maxHour?: number // Maximum hour (1-36, capped at 12:00 next day)
  intervalMinutes?: number // Time interval in minutes (default: 15)
}

/**
 * Generate time options for the select dropdowns (24-hour format)
 */
function generateTimeOptions(
  minHour: number = 0,
  maxHour: number = 24,
  intervalMinutes: number = 15
): TimeOption[] {
  // Cap maxHour at 36 (12:00 next day) for overnight scenarios
  const cappedMaxHour = Math.min(maxHour, 36);
  const options: TimeOption[] = []

  // Calculate total intervals using capped maxHour
  const totalHours = cappedMaxHour - minHour
  const intervalsPerHour = 60 / intervalMinutes
  const totalIntervals = totalHours * intervalsPerHour

  for (let i = 0; i < totalIntervals; i++) {
    const totalMinutes = minHour * 60 + (i * intervalMinutes)
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    // Handle 24+ hours for overnight display
    const displayHours = hours
    const value = `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`

    // Format label in 24-hour format
    let label: string
    if (hours === 24) {
      label = value // Keep 24:00
    } else if (hours > 24) {
      const nextDayHour = hours - 24
      label = `${nextDayHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
    } else {
      label = value
    }

    options.push({ value, label })
  }

  return options
}

/**
 * Generate end time options starting from 12:00 to 12:00 next day (24-hour format)
 */
function generateEndTimeOptions(intervalMinutes: number = 60): TimeOption[] {
  const options: TimeOption[] = []

  // First part: 12:00 to 24:00 (same day)
  for (let hour = 12; hour <= 24; hour++) {
    for (let minute = 0; minute < 60; minute += intervalMinutes) {
      if (hour === 24 && minute > 0) break; // Stop at 24:00, no 24:15, etc.

      const value = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      options.push({ value, label: value })
    }
  }

  // Second part: 01:00 to 12:00 next day
  for (let hour = 25; hour <= 36; hour++) { // 25 = 01:00 next day, 36 = 12:00 next day
    for (let minute = 0; minute < 60; minute += intervalMinutes) {
      if (hour === 36 && minute > 0) break; // Stop at 12:00 next day

      const value = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      const nextDayHour = hour - 24
      const label = `${nextDayHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      options.push({ value, label })
    }
  }

  return options
}

/**
 * Convert internal time format (25:00) to display format (24-hour)
 */
function formatSelectedTime(timeValue: string): string {
  const [hour, minute] = timeValue.split(':').map(Number);

  if (hour === 24) {
    return `${hour}:${minute.toString().padStart(2, '0')}`; // Keep 24:00
  }
  if (hour > 24) {
    const nextDayHour = hour - 24;
    return `${nextDayHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  }
  return timeValue; // Hours 0-23 remain unchanged
}

/**
 * Validate that end time is after start time
 */
function validateTimeRange(startTime: string, endTime: string): boolean {
  const [startHour, startMin] = startTime.split(':').map(Number)
  const [endHour, endMin] = endTime.split(':').map(Number)

  const startMinutes = startHour * 60 + startMin
  const endMinutes = endHour * 60 + endMin

  return endMinutes > startMinutes
}

export function TimeRangeSelector({
  value,
  onChange,
  onFormatToggle,
  showFormatToggle = false,
  disabled = false,
  className = '',
  minHour = 0,
  maxHour = 24,
  intervalMinutes = 15
}: TimeRangeSelectorProps) {

  const timeOptions = generateTimeOptions(minHour, maxHour, intervalMinutes)
  const endTimeOptions = generateEndTimeOptions(intervalMinutes)

  const handleStartTimeChange = (newStartTime: string) => {
    const newValue = { ...value, startTime: newStartTime }

    // Auto-adjust end time if it becomes invalid
    if (!validateTimeRange(newStartTime, value.endTime)) {
      // Find next valid end time (at least 15 minutes after start)
      const [startHour, startMin] = newStartTime.split(':').map(Number)
      const minEndMinutes = startHour * 60 + startMin + intervalMinutes
      const minEndHour = Math.floor(minEndMinutes / 60)
      const minEndMin = minEndMinutes % 60
      const newEndTime = `${minEndHour.toString().padStart(2, '0')}:${minEndMin.toString().padStart(2, '0')}`

      newValue.endTime = newEndTime
    }

    onChange(newValue)
  }

  const handleEndTimeChange = (newEndTime: string) => {
    const newValue = { ...value, endTime: newEndTime }

    // Auto-adjust start time if it becomes invalid
    if (!validateTimeRange(value.startTime, newEndTime)) {
      // Find previous valid start time (at least 15 minutes before end)
      const [endHour, endMin] = newEndTime.split(':').map(Number)
      const maxStartMinutes = endHour * 60 + endMin - intervalMinutes
      const maxStartHour = Math.floor(maxStartMinutes / 60)
      const maxStartMin = maxStartMinutes % 60
      const newStartTime = `${Math.max(0, maxStartHour).toString().padStart(2, '0')}:${maxStartMin.toString().padStart(2, '0')}`

      newValue.startTime = newStartTime
    }

    onChange(newValue)
  }

  // Filter options to prevent invalid selections
  const validStartOptions = timeOptions.filter(option => {
    return validateTimeRange(option.value, value.endTime) || option.value === value.startTime
  })

  const validEndOptions = endTimeOptions.filter(option => {
    return validateTimeRange(value.startTime, option.value) || option.value === value.endTime
  })

  return (
    <div className={`space-y-4 ${className}`}>
      {showFormatToggle && (
        <div className="flex items-center justify-between">
          <Label htmlFor="show-24h-toggle" className="text-sm font-medium">
            Show 24 Hours
          </Label>
          <Switch
            id="show-24h-toggle"
            checked={value.startTime === '00:00' && value.endTime === '24:00'}
            onCheckedChange={(checked) => {
              if (checked) {
                // Set to full 24-hour range
                onChange({ startTime: '00:00', endTime: '24:00' })
              }
              // When unchecked, keep current values (user can adjust manually)
              onFormatToggle?.(checked)
            }}
            disabled={disabled}
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="start-time" className="text-sm font-medium">
            Start Time
          </Label>
          <Select
            value={value.startTime}
            onValueChange={handleStartTimeChange}
            disabled={disabled}
          >
            <SelectTrigger id="start-time">
              <SelectValue placeholder="Select start time">
                {formatSelectedTime(value.startTime)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {validStartOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="end-time" className="text-sm font-medium">
            End Time
          </Label>
          <Select
            value={value.endTime}
            onValueChange={handleEndTimeChange}
            disabled={disabled}
          >
            <SelectTrigger id="end-time">
              <SelectValue placeholder="Select end time">
                {formatSelectedTime(value.endTime)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {validEndOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

    </div>
  )
}